import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, RefObject, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { ChatMessage } from "@/types/base"
import { extractAssistantToolCalls, Payload, ToolCallExtract } from "@/lib/chat/chat-utils"
import { createClientLogger } from "@/utils/logger/clientLogger"
import { v4 as uuidv4 } from "uuid"

const logger = createClientLogger('realtime.chatMsg')

const appliesToCurrentChat = (selectedChatId:string | null, id: string): boolean => {
	if (!selectedChatId) return false
	return id === selectedChatId
}

const appliesToChatMessages = (chatMessages: ChatMessage[], id: string): boolean => {
	return chatMessages.some(cm => cm.message.id === id)
}

type Row = Tables<'messages'>
type PartialRow = Partial<Row> & { id: string }

function mergeRow<T extends { id: string }>(prev: T, update: Partial<T>): T {
	return { ...prev, ...update }
}

function getChangedFields<T extends { id: string }>(
  oldRow: Partial<T>,
  newRow: Partial<T>
): (keyof T)[] {
  return Object.keys(newRow).filter(
    key =>
      key !== 'id' &&
      Object.prototype.hasOwnProperty.call(oldRow, key) &&
      oldRow[key as keyof T] !== newRow[key as keyof T]
  ) as (keyof T)[]
}

// function getChangedFields(oldRow: Row, newRow: Row): (keyof Row)[] {
//   return (Object.keys(newRow) as (keyof Row)[]).filter(
//     key => oldRow[key] !== newRow[key]
//   )
// }

/**
 * This method handle update to track updates made by the billing API that picks up the payload.
 * It doesn't handle since messages can't really be deleted
 * @param workspaceId
 * @param setChatMessages
 * @returns handler config
 */
export function makeChatMessageInsertHandler(
	selectedChatIdRef: RefObject<string | null>,
	setChatMessagesRef: RefObject<Dispatch<SetStateAction<ChatMessage[]>>>,
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `message-insert-${uuid}`,
		channelName: `message-insert-${uuid}`,
		events: ['INSERT'],
		schema: 'public',
		table: 'messages',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			const selectedChatId = selectedChatIdRef.current
			const setChatMessages = setChatMessagesRef.current
			if (payload.eventType == "INSERT") {
				const message = payload.new as Row
				if (appliesToCurrentChat(selectedChatId, message.chat_id)) {
					setChatMessages(prev => {
						if (prev.some(p => p.message.id === message.id)) return prev
						logger.info(`Handling message Insert:${printObject(message)}`)
						return [
							{
								message,
								fileItems: [],
								toolCalls: []
							},
							...prev
						]
					})
				}
			}
		},
		inactivityThreshold: 1_800_000
	}
}

/**
 * This method handle update to track updates made by the billing API that picks up the payload.
 * It doesn't handle since messages can't really be deleted
 * @param setChatMessages
 * @returns handler config
 */
export function makeChatMessageChangeHandler(
	setChatMessagesRef: RefObject<Dispatch<SetStateAction<ChatMessage[]>>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `message-changes-${uuid}`,
		channelName: `message-changes-${uuid}`,
		events: ['UPDATE'],
		schema: 'public',
		table: 'messages',
		handler: async (payload: RealtimePostgresChangesPayload<PartialRow>) => {
			const setChatMessages = setChatMessagesRef.current
			if (payload.eventType === 'UPDATE') {
				const partialMessage = payload.new
				const oldPartialMessage = payload.old

				setChatMessages(prev =>
					prev.map(cm => {
						if (cm.message.id !== partialMessage.id) return cm

						const mergedMessage = mergeRow(cm.message, partialMessage)
						const changedFields = getChangedFields(oldPartialMessage ?? {}, partialMessage)
						let toolCalls: ToolCallExtract[] = []

						if (changedFields.includes('payload') && partialMessage.payload) {
							toolCalls = extractAssistantToolCalls(partialMessage.payload as Payload).filter(Boolean)
						}

						const mergedToolCalls = [...cm.toolCalls, ...toolCalls].filter((tc, idx, arr) =>
							arr.findIndex(t => t.toolName === tc.toolName && t.toolCallId === tc.toolCallId) === idx
						)

						logger.info(`Handling message update:${printObject(mergedMessage)}`)

						return {
							...cm,
							message: mergedMessage,
							toolCalls: mergedToolCalls
						}
					})
				)
			}
		},
		inactivityThreshold: 1_800_000
	}
}

//
/**
 * This method doesn't handle update since the relationship join table is not designed to mutate in that way
 * @param workspaceId
 * @param setPresets
 * @returns
 */
export function makeChatMessageFileItemHandler(
	chatMessagesRef: RefObject<ChatMessage[]>,
	setChatMessagesRef: RefObject<Dispatch<SetStateAction<ChatMessage[]>>>,
	setChatFileItemsRef: RefObject<Dispatch<SetStateAction<string[]>>>,
): HandlerConfig<Tables<'message_file_items'>> {
	const uuid = uuidv4()
	return {
		key: `message_file_items-changes-${uuid}`,
		channelName: `message_file_items-changes-${uuid}`,
		events: ['INSERT'],
		schema: 'public',
		table: 'message_file_items',
		handler: async (payload: RealtimePostgresChangesPayload<Tables<'message_file_items'>>) => {
			const setChatMessages = setChatMessagesRef.current
			const setChatFileItems = setChatFileItemsRef.current
			if (payload.eventType == "INSERT") {
				const msgFileItem = payload.new as Tables<"message_file_items">
				const messageId = msgFileItem.message_id
				const fileItemId = msgFileItem.file_item_id
				const chatMessages = chatMessagesRef.current

				if (appliesToChatMessages(chatMessages, messageId)) {
					logger.info(`Handling messsage file item Insert:${printObject(msgFileItem)}`)

					setChatMessages(prev =>
						prev.map(cm =>
							cm.message.id === messageId
								? cm.fileItems.includes(fileItemId)
									? cm
									: { ...cm, fileItems: [...cm.fileItems, fileItemId] }
								: cm
						)
					)

					setChatFileItems(prev => {
						if (prev.some(p => p === fileItemId)) return prev
						return [...prev, fileItemId]
					})
				}
			}
			// else if (payload.eventType == "DELETE") {
			// 	const msgFileItem = payload.old as Tables<"message_file_items">
			// 	const id = presetWs.preset_id
			// 	logger.info(`Handling preset Delete:${id}`)
			// 	//here can't check if in workspace but if it is it will be found and removed
			// 	setPresets((prevItems: any) => prevItems.filter((prevItem: any) => prevItem.id !== id))
			// }
		},
		inactivityThreshold: 1_800_000
	}
}